"use client";

import { Clock, PartyPopper, X } from "lucide-react";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { useLocalStorage } from "react-use";
import { useUserPlanQuery } from "@/features/users/api/user-plan-options";
import usePricingModal, { PAYWALL_TYPES } from "@/lib/hooks/use-pricing-modal";
import { useI18n } from "@/locales/client";

export function PromotionalBanner() {
  const [isVisible, setIsVisible] = useLocalStorage("promotionalBanner", true);
  const [timeLeft, setTimeLeft] = useState<string>("");
  const { onOpen } = usePricingModal();
  const t = useI18n();
  const { data: userPlan } = useUserPlanQuery();

  useEffect(() => {
    const calculateTimeLeft = () => {
      // Set target date to August 31, 2025 at 11:59 PM
      const targetDate = new Date("2025-08-31T23:59:59");
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        );
        const minutes = Math.floor(
          (difference % (1000 * 60 * 60)) / (1000 * 60),
        );
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        return `${days}d ${hours}h ${minutes}m ${seconds}s`;
      }
      return "Expired";
    };

    // Calculate initial time
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      // Hide banner if expired
      if (newTimeLeft === "Expired") {
        setIsVisible(false);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [setIsVisible]);

  if (!isVisible || !userPlan || userPlan.hasPremiumAccess) return null;

  return (
    <div className="bg-gradient-to-r from-green-50 to-green-100 border-b border-green-200 px-4 py-1 w-full">
      <div className="flex items-center justify-between text-sm w-full">
        <div className="flex items-center justify-center gap-x-4 gap-y-2 flex-grow flex-wrap">
          {/* Discount Text */}
          <div className="flex items-center gap-1">
            <span className="font-semibold flex items-center gap-1 text-[#3CB371] dark:text-[#7DFF97]">
              <PartyPopper
                className="h-4 w-4 flex-shrink-0"
                aria-hidden="true"
              />
              {t("promotionalBanner.discount")}
            </span>
            <span className="font-medium capitalize text-[#3CB371] dark:text-[#7DFF97]">
              {t("promotionalBanner.title")}
            </span>
          </div>

          {/* Timer with Clock Background */}
          <div className="flex items-center gap-1 px-2 py-1 rounded-md border border-green-500/20 backdrop-blur-sm text-[#3CB371] dark:text-[#7DFF97] bg-green-500/10">
            <Clock className="h-4 w-4" aria-hidden="true" />
            <span className="font-medium text-xs">{timeLeft}</span>
          </div>

          {/* CTA Button */}
          <button
            type="button"
            onClick={() => {
              posthog.capture("paywall_shown", {
                paywallType: PAYWALL_TYPES.FALL_SALE,
                userId: userPlan.userId,
              });
              onOpen({ paywallType: PAYWALL_TYPES.FALL_SALE });
            }}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 underline-offset-4 hover:underline h-9 rounded-lg font-semibold px-0 underline text-[#3CB371] dark:text-[#7DFF97] hover:text-green-700 dark:hover:text-green-300"
          >
            {t("promotionalBanner.claimDiscount")}
          </button>
        </div>

        {/* Close Button */}
        <button
          type="button"
          onClick={() => setIsVisible(false)}
          className="text-green-600 hover:text-green-800 p-1 rounded-md hover:bg-green-200 transition-colors"
          aria-label="Close banner"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
